import React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Checkbox,
    Typography,
} from "@mui/material";
import { Button } from "@aidergroup/ui-v2";
import { i18n } from "@/utils/i18n";
import { Attachment } from "@/types/report";

const REPORT_SECTIONS = [
    "FRONT_PAGE",
    "ECONOMIC_SUMMARY",
    "ASSETS_DEBTS",
    "INCOMES_EXPENSES",
    "VERIFICATIONS",
    "LEDGER",
];

interface Props {
    open: boolean;
    onClose(): void;
    attachments: Attachment[];
    hasSectionMap: boolean;
    onDownload(pages: string[], attachmentMd5s: string[]): Promise<void>;
    downloading: boolean;
}

const RegistryExportDialog: React.FC<Props> = ({
    open,
    onClose,
    attachments,
    hasSectionMap,
    onDownload,
    downloading,
}) => {
    const [selectedPages, setSelectedPages] = React.useState<string[]>(REPORT_SECTIONS);
    const [selectedAttachments, setSelectedAttachments] = React.useState<string[]>([]);

    React.useEffect(() => {
        if (open) {
            setSelectedPages(REPORT_SECTIONS);
            setSelectedAttachments([]);
        }
    }, [open]);

    const togglePage = (page: string) => {
        setSelectedPages(prev =>
            prev.includes(page) ? prev.filter(i => i !== page) : [...prev, page],
        );
    };

    const toggleAttachment = (md5: string) => {
        setSelectedAttachments(prev =>
            prev.includes(md5) ? prev.filter(i => i !== md5) : [...prev, md5],
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{i18n.t("registryExport.dialogTitle")}</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    {i18n.t("registryExport.dialogHelp")}
                </Typography>
                <FormGroup sx={{ mb: 2 }}>
                    <FormLabel sx={{ mb: 1 }}>{i18n.t("registryExport.sectionsTitle")}</FormLabel>
                    {!hasSectionMap && (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {i18n.t("registryExport.noSectionMap")}
                        </Typography>
                    )}
                    {REPORT_SECTIONS.map(page => (
                        <FormControlLabel
                            key={page}
                            control={
                                <Checkbox
                                    checked={hasSectionMap ? selectedPages.includes(page) : true}
                                    disabled={!hasSectionMap}
                                    onChange={() => togglePage(page)}
                                />
                            }
                            label={i18n.t(`reportPreview.pages.${page}`)}
                        />
                    ))}
                </FormGroup>
                {attachments.length > 0 && (
                    <FormGroup>
                        <FormLabel sx={{ mb: 0.5 }}>
                            {i18n.t("registryExport.attachmentsTitle")}
                        </FormLabel>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                            {i18n.t("registryExport.attachmentsHelp")}
                        </Typography>
                        {attachments.map(attachment => (
                            <FormControlLabel
                                key={attachment.md5}
                                control={
                                    <Checkbox
                                        checked={selectedAttachments.includes(attachment.md5)}
                                        onChange={() => toggleAttachment(attachment.md5)}
                                    />
                                }
                                label={attachment.name}
                            />
                        ))}
                    </FormGroup>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button variant="outlined" color="error" onClick={onClose}>
                    {i18n.t("button.cancel")}
                </Button>
                <Button
                    variant="contained"
                    onClick={async () => {
                        await onDownload(selectedPages, selectedAttachments);
                        onClose();
                    }}
                    disabled={downloading || selectedPages.length === 0}
                >
                    {i18n.t("registryExport.download")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RegistryExportDialog;
